import React, { useCallback, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useDropzone } from "react-dropzone";
import { If, Then, Else } from "react-if";

import { uploadPatientFilesSagaAction } from "../../sagas/saga-fileUpload";
import validate from "./validate";
import CustomButton from "../customButton";
import { setPopup } from "../../actions/popupAction";
import CustomNotification from "../notification";
import LoadingSpinner from "../loadingSpinner";

import {
  CancelUploadIcon,
  ErrorYellowIcon,
  uploadFile,
} from "../../assets/svg";
import TelevoxLogo from "../../images/logos/televox-housecalls-pro-logo.svg";
import NextgenLogo from "../../images/logos/nextgen-logo.png";
import veradigmLogo from "../../images/login/veradigm-login-logo.png";
import CernerLogo from "../../images/logos/cerner-logo.png";
import SuccessIcon from "../../images/upload/success.svg";
import UploadedIcon from "../../images/upload/uploaded.svg";
import "./styles.scss";
import {
  CERNER_EHR_WHITELABEL,
  NEXTGEN_EHR_WHITELABEL,
  uploadFileSize,
  VERADIGM_EHR_WHITELABEL,
} from "../../constants/constants";

const PatientUpload = (props) => {
  const [state, setState] = useState({
    files: [],
    patientId: props?.match?.params?.patientid,
    departmentId: props?.match?.params?.departmentId,
    uploadStatus: null,
    error: null,
    expiration: null,
    uploading: false,
    totalFileCount: 0,
    loading: true,
  });

  const uploadRef = useRef(false);

  const handleUploadProgress = (percentCompleted) => {
    setState((st) => ({
      ...st,
      percentCompleted,
    }));
  };

  const handleFileUpload = () => {
    if (uploadRef && uploadRef.current) return;

    if (state.uploadStatus) {
      setState((st) => ({
        ...st,
        uploadStatus: null,
      }));
    }

    if (state.files.length === 0) {
      return;
    }

    setState((st) => ({
      ...st,
      uploading: true,
    }));

    uploadRef.current = true;

    const object = {
      patientId: state.patientId,
      departmentId: state.departmentId,
      files: state.files,
    };

    const uploadSuccess = () => {
      setState((st) => ({
        ...st,
        percentCompleted: 100,
        uploadStatus: "success",
      }));
    };

    const uploadFailure = () => {
      setState((st) => ({
        ...st,
        percentCompleted: 0,
        uploadStatus: "error",
      }));
    };

    props.uploadPatientFiles(
      object,
      (result) => {
        if (result && result.status === 200) {
          uploadSuccess();

          props.setPopup({
            customPopupMessage: true,
            message: "Files uploaded successfully!",
            messageType: "success",
          });
        } else {
          uploadFailure();

          props.setPopup({
            customPopupMessage: true,
            message: result.data.message || "Something went wrong!",
            messageType: "error",
          });
        }

        setState((st) => ({
          ...st,
          uploading: false,
        }));

        uploadRef.current = false;
      },
      () => handleUploadProgress()
    );
  };

  const removeItem = (index) => {
    const files = state.files;

    if (files && files.length) {
      files.splice(index, 1);
    }

    if (files.length < 1) {
      setState((st) => ({
        ...st,
        files,
        totalFileCount: 0,
      }));
    }

    setState((st) => ({
      ...st,
      files,
      totalFileCount: 0,
    }));
  };

  const humanize = (value) => {
    if (value > 1000000) return `${(value / 1000000).toFixed(1)}MB`;
    if (value > 1000) return `${(value / 1000).toFixed(1)}KB`;
    else return `${value}B`;
  };

  const populateFileNames = (maxUploadSize) => {
    let total = 0;
    let validTotalSize = true;

    return (
      <>
        <section className="files-wrapper">
          {state.files.map((value, index) => {
            total += value.size;
            validTotalSize = total > maxUploadSize;

            return (
              <article key={index} className="file-row">
                <div className="file-name">{value.name.substr(0, 20)}</div>

                <section className="file-row-info">
                  <div className="total-size">{humanize(value.size)}</div>

                  {state.uploadStatus === "success" ? null : (
                    <div
                      type="button"
                      onClick={() => removeItem(index)}
                      className="cancel-button"
                    >
                      {CancelUploadIcon}
                    </div>
                  )}
                </section>
              </article>
            );
          })}
        </section>

        {total ? (
          <section className="total-row">
            <CustomButton
              htmlType="submit"
              type="primary"
              onClick={handleFileUpload}
              title="Upload Files"
              key="submit"
              disabled={validTotalSize || state.uploading ? true : false}
              className={
                validTotalSize || state.uploading ? "disable-button" : ""
              }
            />

            <p
              className={`total-size ${validTotalSize && "text-danger"}`}
              key={state.files.length + 1}
            >
              Total File Size: {humanize(total)}
            </p>
          </section>
        ) : (
          ""
        )}
      </>
    );
  };

  const getFiles = (newFiles) => {
    const files = state.files;
    const paths = files.map((file) => file.path);

    newFiles.forEach((file) => {
      if (!paths.includes(file.path) && files.length < 7) files.push(file);
    });

    setState((st) => ({
      ...st,
      files,
      uploadStatus: null,
      totalFileCount: newFiles.length,
    }));
  };

  useEffect(() => {
    validate(state.patientId, state.departmentId)
      .then((response) => {
        if (response) {
          if (response.message) {
            setState((st) => ({
              ...st,
              error: response.message,
              loading: false,
            }));
          } else if (response.data) {
            setState((st) => ({
              ...st,
              error: response.data.error,
              expired: response.data.expired,
              loading: false,
            }));
          }
        }
      })
      .catch((err) => {
        setState((st) => ({
          ...st,
          error: "This URL is invalid",
          loading: false,
        }));
      });
  }, []);

  let error = state.error;
  let expired = state.expired;

  if (expired) error = "This URL has expired";

  if (expired) {
    const errorMsg = `${error}. Please contact your provider to send you another link to upload files.`;
    return (
      <div
        className="patient-upload-main-container"
        data-testid="patient-upload-page-container"
      >
        <img
          className="main-logo"
          id="main-logo"
          src={TelevoxLogo}
          alt="Televox"
        />

        <section div className="patient-upload-card-wrapper">
          <article className="files-upload-succes-container">
            <img className="icon" src={UploadedIcon} alt="Televox" />
            <h4 className="heading">The link has expired</h4>
            <h4 className="sub-heading">{errorMsg}</h4>
          </article>
        </section>
      </div>
    );
  }

  const getEhrLogo = () => {
    return NEXTGEN_EHR_WHITELABEL
      ? NextgenLogo
      : CERNER_EHR_WHITELABEL
        ? CernerLogo
        : VERADIGM_EHR_WHITELABEL
          ? veradigmLogo
          : TelevoxLogo;
  };

  if (error) {
    return (
      <div
        className="patient-upload-main-container"
        data-testid="patient-upload-page-container"
      >
        <img
          className="main-logo"
          id="main-logo"
          src={getEhrLogo()}
          alt="Televox"
        />

        <section div className="patient-upload-card-wrapper">
          <article className="files-upload-succes-container">
            <img className="icon" src={UploadedIcon} alt="Televox" />
            <h4 className="heading">Invalid request</h4>
            <h4 className="sub-heading">{state.error}</h4>
          </article>
        </section>
      </div>
    );
  }

  const uploadStatus = state.uploadStatus;
  const maxUploadSize = uploadFileSize;

  const { customPopupMessage, messageType, message } = props.popupReducer;

  return (
    <>
      {customPopupMessage && (
        <CustomNotification messageType={messageType} message={message} />
      )}

      <div
        className="patient-upload-main-container"
        data-testid="patient-upload-page-container"
      >
        <img
          className="main-logo"
          id="main-logo"
          src={getEhrLogo()}
          alt="Televox"
        />

        <If condition={uploadStatus === "success"}>
          <Then>
            <section div className="patient-upload-card-wrapper">
              <article className="files-upload-succes-container">
                <img className="icon" src={SuccessIcon} alt="Televox" />
                <h4 className="heading">
                  Your files have been uploaded successfully
                </h4>
              </article>
            </section>
          </Then>
          <Else>
            <div className="patient-upload-card-wrapper">
              <If condition={state.loading}>
                <Then>
                  <LoadingSpinner
                    type="small"
                    customClassName="white-background"
                  />
                </Then>
                <Else>
                  <div className="patient-upload-card">
                    <div className="files-list-wrapper">
                      <h4 className="heading">Upload Files</h4>
                      <ul className="docs-list">
                        <li className="docs">
                          Driving License (front and back)
                        </li>
                        <li className="docs">
                          Insurance Card (front and back)
                        </li>
                        <li className="docs">Consent Form</li>
                      </ul>
                      <p className="caption">
                        Note: Maximum file upload limit is 7 and upload size is
                        8mb
                      </p>
                      <If condition={state.totalFileCount > 7}>
                        <Then>
                          <section className="limit-exceed">
                            <span className="message">
                              {ErrorYellowIcon} You have selected{" "}
                              {state.totalFileCount} files out of which first 7
                              files will be uploaded
                            </span>
                          </section>
                        </Then>

                        <Else>{null}</Else>
                      </If>
                    </div>

                    <div
                      className={`files-upload-wrapper ${state.files.length >
                        0 && "files-upload-expanded"}`}
                    >
                      <FileUpload getFiles={getFiles} />

                      {state.files.length > 0 ? (
                        <div className="selected-files-container">
                          {populateFileNames(maxUploadSize)}
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </Else>
              </If>
            </div>
          </Else>
        </If>
      </div>
    </>
  );
};

const FileUpload = (props) => {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length) {
      props.getFiles(acceptedFiles);
    } else {
      return;
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*, .doc, .pdf, .txt",
    multiple: true,
  });

  return (
    <div className="file-uploader" {...getRootProps()} tabIndex={-1}>
      {uploadFile}

      <input {...getInputProps()} />

      <p className="upload-text">
        Drag and Drop or <span className="choose-file">Choose File</span>
      </p>
    </div>
  );
};

const mapStateToProps = (state) => ({
  fileNames: state.fileNames,
  uploadStatus: state.uploadStatus,
  popupReducer: state.popupReducer,
});

const mapDispatchToProps = (dispatch) => {
  return {
    uploadPatientFiles: (object, cb, progressCb) => {
      dispatch(uploadPatientFilesSagaAction(object, cb, progressCb));
    },
    setPopup: (data) => dispatch(setPopup(data)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PatientUpload);
